import { useCallback } from 'react';
import { models } from 'powerbi-client';

const useReportZoom = (reportRef, containerRef) => {
  const calculateMargin = () => {
    if (window.innerWidth <= 768) return 10;
    if (window.innerWidth > 2560) return 60;  // Margem maior para telas muito grandes
    if (window.innerWidth > 1920) return 40;
    return 20;
  };

  const calculateZoomLevel = (containerDimensions, pageDimensions) => {
    const margin = calculateMargin();
    
    // Cálculo base da razão de aspecto
    const widthRatio = (containerDimensions.width - margin * 2) / pageDimensions.width;
    const heightRatio = (containerDimensions.height - margin * 2) / pageDimensions.height;
    let zoomLevel = Math.min(widthRatio, heightRatio);

    // Fator de redução base mais conservador
    const reductionFactor = 0.80; // Reduzido para 80% do tamanho original
    zoomLevel *= reductionFactor;

    // Ajustes específicos por tamanho de tela
    if (window.innerWidth <= 768) {
      zoomLevel *= 0.90;
    } else if (window.innerWidth <= 1024) {
      zoomLevel *= 0.93;
    } else if (window.innerWidth <= 1440) {
      zoomLevel *= 0.90;
    } else if (window.innerWidth <= 1920) {
      zoomLevel *= 0.85; // Redução maior para telas grandes
    } else if (window.innerWidth <= 2560) {
      zoomLevel *= 0.80; // Redução ainda maior para telas muito grandes
    } else {
      zoomLevel *= 0.75; // Redução máxima para telas extremamente grandes
    }

    // Limites de zoom ajustados
    const minZoom = 0.3;
    const maxZoom = window.innerWidth > 1920 ? 0.85 : 0.95; // Limite máximo menor para telas grandes
    
    return Math.min(Math.max(zoomLevel, minZoom), maxZoom);
  };

  const getActivePageSize = async (report) => {
    try {
      const pages = await report.getPages();
      if (!Array.isArray(pages) || pages.length === 0) {
        throw new Error('No pages available');
      }

      const activePage = pages.find(p => p.isActive);
      if (!activePage) {
        throw new Error('No active page found');
      }

      if (!activePage.getPageSize || typeof activePage.getPageSize !== 'function') {
        throw new Error('getPageSize method not available');
      }

      return {
        page: activePage,
        size: await activePage.getPageSize()
      };
    } catch (err) {
      console.error('Error getting page size:', err);
      return null;
    }
  };

  const updateZoom = useCallback(async () => {
    if (!reportRef.current || !containerRef.current) {
      return null;
    }

    try {
      const report = reportRef.current;
      const pageInfo = await getActivePageSize(report);

      if (!pageInfo) {
        console.warn('Could not get page size, skipping zoom update');
        return null;
      }

      const { page, size: pageSize } = pageInfo;

      const containerDimensions = {
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight
      };

      if (!pageSize || !pageSize.width || !pageSize.height) {
        console.warn('Invalid page dimensions', pageSize);
        return null;
      }

      const zoomLevel = calculateZoomLevel(containerDimensions, pageSize);

      // Sistema de retry aprimorado com backoff exponencial
      const maxRetries = 3;
      for (let i = 0; i < maxRetries; i++) {
        try {
          await report.setZoom(zoomLevel);
          break;
        } catch (err) {
          if (i === maxRetries - 1) throw err;
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 500));
        }
      }

      // Atualiza o estado visual com retry
      try {
        await page.setVisualDisplayState(models.VisualContainerDisplayMode.Hidden);
        await page.setVisualDisplayState(models.VisualContainerDisplayMode.Visible);
      } catch (err) {
        console.warn('Could not update visual state:', err);
      }


      return zoomLevel;
    } catch (err) {
      console.error('Error updating zoom:', err);
      return null;
    }
  }, [reportRef, containerRef]);

  const updateZoomWithDelay = useCallback((delay = 100) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        updateZoom()
          .then(resolve)
          .catch((err) => {
            console.warn('Error in delayed zoom update:', err);
            resolve(null);
          });
      }, delay);
    });
  }, [updateZoom]);

  return {
    updateZoom,
    updateZoomWithDelay
  };
};

export default useReportZoom;